import { useApolloClient, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useToaster } from '../../../core/components/Toast.tsx';
import { CategoryFormData } from '../../../customer/pages/SmartInbox/CreateCustomCategoryModal.tsx';
import { buildCategoryActions } from '../../../customer/pages/SmartInbox/customCategoryMapping.ts';
import { RecommendedCategory } from '../../../customer/pages/SmartInbox/RecommendedCategoriesGrid.tsx';
import {
    ADD_RECOMMENDED_CATEGORIES,
    CREATE_CUSTOM_CATEGORY,
    GET_MAILBOX_CATEGORY_IDS,
} from '../../../customer/queries.ts';

export const useCategoryCreate = (integrationId: string, channelData: Record<string, any> | undefined) => {
    const { t } = useTranslation('mailboxes');
    const { publishToast } = useToaster();
    const apolloClient = useApolloClient();

    const [createCustomCategoryMutation, { loading: creating }] = useMutation(CREATE_CUSTOM_CATEGORY);
    const [addRecommendedCategoriesMutation, { loading: addingRecommended }] = useMutation(ADD_RECOMMENDED_CATEGORIES);

    // the create mutations don't return the new category, so it's the id that wasn't there before
    const fetchCategoryIds = async (): Promise<string[]> => {
        const { data } = await apolloClient.query({
            query: GET_MAILBOX_CATEGORY_IDS,
            variables: { id: integrationId },
            fetchPolicy: 'network-only',
        });
        return (data?.smartInboxIntegration?.customCategories || []).map((category: { id: string }) => category.id);
    };

    const withCreatedIds = async (create: () => Promise<unknown>): Promise<string[]> => {
        const idsBefore = new Set(await fetchCategoryIds());
        await create();
        return (await fetchCategoryIds()).filter((id) => !idsBefore.has(id));
    };

    const createCustomCategory = async (formData: CategoryFormData): Promise<string[] | null> => {
        const actions = buildCategoryActions(formData, channelData);
        try {
            const createdIds = await withCreatedIds(() =>
                createCustomCategoryMutation({
                    variables: {
                        integrationId,
                        name: formData.name,
                        detectionCriteria: formData.detectionCriteria,
                        supportingKeywords: formData.supportingKeywords,
                        actions,
                    },
                })
            );
            publishToast({ description: t('detail.categoriesTab.createSuccess'), status: 'success' });
            return createdIds;
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.categoriesTab.saveError'), status: 'error' });
            return null;
        }
    };

    const addRecommendedCategories = async (recommended: RecommendedCategory[]): Promise<string[] | null> => {
        try {
            const createdIds = await withCreatedIds(() =>
                addRecommendedCategoriesMutation({
                    variables: { integrationId, recommendedCategoryIds: recommended.map((r) => r.id) },
                })
            );
            publishToast({ description: t('detail.categoriesTab.createSuccess'), status: 'success' });
            return createdIds;
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.categoriesTab.saveError'), status: 'error' });
            return null;
        }
    };

    return { createCustomCategory, addRecommendedCategories, creating, addingRecommended };
};

export default useCategoryCreate;
